import { definedMap } from "@glideapps/ts-necessities";
import flatMap from "lodash/flatMap";

import { logError } from "../../debug-print";
import { DeliverEmailFromActionBody } from "../../firebase-function-types";
import { isArray, isValidEmailAddress, mapFilterUndefined } from "../../Support";
import { asString, getValueFromValueProducer, isLoadedDefined, loadedDefinedMap } from "../component-helpers";
import { DeserializationSpec } from "../computation-types";
import {
    ActionResult,
    ActionSource,
    Context,
    EnvironmentBoundEvaluable,
    OnlineOnlyAction,
    RenderEnvironment,
} from "../types";
import { evalAndRunAction } from "./support";

export function splitEmailAddresses(str: string): string[] {
    return str.split(",").filter(isValidEmailAddress);
}

export class DeliverEmailAction extends OnlineOnlyAction {
    constructor(
        private readonly _recipient: EnvironmentBoundEvaluable,
        private readonly _subject: EnvironmentBoundEvaluable,
        private readonly _body: EnvironmentBoundEvaluable,
        private readonly _replyTo: EnvironmentBoundEvaluable | undefined,
        private readonly _onSuccess: EnvironmentBoundEvaluable | undefined,
        private readonly _onFailure: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    private evalArgs(
        ctx: Context,
        actionSource: ActionSource
    ):
        | {
              readonly recipients: readonly string[];
              readonly subject: string;
              readonly body: string;
              readonly replyTo: string | undefined;
          }
        | undefined {
        const recipientValue = definedMap(this._recipient.eval(ctx, actionSource), getValueFromValueProducer);
        const subjectValue = definedMap(this._subject.eval(ctx, actionSource), getValueFromValueProducer);
        const bodyValue = definedMap(this._body.eval(ctx, actionSource), getValueFromValueProducer);
        const replyToValue = definedMap(this._replyTo?.eval(ctx, actionSource), getValueFromValueProducer);

        if (!isLoadedDefined(recipientValue) || !isLoadedDefined(subjectValue) || !isLoadedDefined(bodyValue)) {
            return undefined;
        }

        let recipients: string[];
        if (isArray(recipientValue)) {
            const items = mapFilterUndefined(recipientValue, vp =>
                loadedDefinedMap(getValueFromValueProducer(vp), asString)
            );
            recipients = flatMap(items, splitEmailAddresses);
        } else {
            recipients = splitEmailAddresses(asString(recipientValue));
        }

        if (recipients.length === 0) {
            logError("Not a valid recipient", recipientValue);
            return undefined;
        }

        return {
            recipients,
            subject: asString(subjectValue),
            body: asString(bodyValue),
            replyTo: loadedDefinedMap(replyToValue, asString),
        };
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const args = this.evalArgs(ctx, actionSource);
        if (args === undefined) return undefined;

        const body: DeliverEmailFromActionBody = {
            ...args,
            appID: env.appEnvironment.appID,
            replyTo: args.replyTo,
        };

        // FIXME: handle response: if successful, call continuation (which we don't have yet)
        const response = await env.appEnvironment.appFacilities.callAuthIfAvailableCloudFunction(
            "deliverEmailFromAction",
            body,
            {}
        );
        if (response?.ok === true) {
            return await evalAndRunAction(ctx, env, actionSource, this._onSuccess);
        } else {
            return await evalAndRunAction(ctx, env, actionSource, this._onFailure);
        }
    }

    public canExecute(ctx: Context): boolean {
        return this.evalArgs(ctx, ActionSource.None) !== undefined;
    }
    static get spec(): DeserializationSpec<"action"> {
        return [
            "DeliverEmail",
            "action",
            ["recipient", "evaluable"],
            ["subject", "evaluable"],
            ["body", "evaluable"],
            ["replyTo", "evaluable", false],
            ["onSuccess", "action", false],
            ["onFailure", "action", false],
        ];
    }
}
